import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EventAttributesCondition from 'components/eventAttributes/EventAttributesCondition';
import { EventAttributeData } from 'services/eventAttributesService/eventAttributesService';
import { getEventAttributesRequest } from '../../../../store/eventAttributes/eventAttributes.action';
import {
  selectEventAttributes,
  selectEventAttributesLoading,
} from '../../../../store/eventAttributes/eventAttributes.selector';
import {
  getEventsRequest,
  sendEventRequest,
} from '../../../../store/events/events.action';
import {
  selectEvents,
  selectSendEventError,
  selectSendEventLoading,
} from '../../../../store/events/events.selector';
import type { Condition } from '../../../../types/segments/segments.types';
import { validateConditions } from '../../../../utils/conditionValidation';
import Button from '../../../ui/Button';
import SelectInput from '../../../ui/SelectInput';
import SendEventModal, {
  type SendEventModalSubmitData,
} from './SendEventModal';

interface TriggerEventPanelProps {
  onClose: () => void;
  onSave: (data: {
    event: string;
    conditions: { [key: string]: Condition[] };
    status?: string;
  }) => void;
  initialEvent?: string;
  initialConditions?: { [key: string]: Condition[] };
  onConditionsChange: (conditions: { [key: string]: Condition[] }) => void;
  conditionsModified?: boolean;
  frequencyModified?: boolean;
}

const TriggerEventPanel: React.FC<TriggerEventPanelProps> = ({
  onClose,
  onSave,
  initialEvent = '',
  initialConditions = {},
  onConditionsChange,
  conditionsModified = false,
  frequencyModified = false,
}) => {
  const dispatch = useDispatch();
  const events = useSelector(selectEvents);
  const eventAttributes = useSelector(selectEventAttributes);
  const eventAttributesLoading = useSelector(selectEventAttributesLoading);
  const sendEventLoading = useSelector(selectSendEventLoading);
  const sendEventError = useSelector(selectSendEventError);

  const [selectedEvent, setSelectedEvent] = useState(initialEvent);
  const [isSendEventModalOpen, setIsSendEventModalOpen] = useState(false);
  const [sendAttempted, setSendAttempted] = useState(false);

  useEffect(() => {
    dispatch(getEventsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (selectedEvent) dispatch(getEventAttributesRequest(selectedEvent));
  }, [selectedEvent, dispatch]);

  const eventOptions = events.map((e) => ({ value: e.event, label: e.event }));
  const attributeOptions =
    eventAttributes?.attributes
      ?.filter((a: EventAttributeData) => !a.isDeleted)
      .map((a: EventAttributeData) => ({ value: a.name, label: a.name })) ?? [];

  const conditionsValidation = validateConditions(initialConditions);
  const allConditionsEmpty = Object.values(initialConditions).every(
    (arr) => !Array.isArray(arr) || arr.length === 0
  );
  const conditionsValid = allConditionsEmpty || conditionsValidation.isValid;
  const isValid = selectedEvent.trim() !== '' && conditionsValid;
  const eventModified = selectedEvent !== initialEvent;
  const hasModified = conditionsModified || eventModified || frequencyModified;

  const handleSave = () => {
    if (isValid) {
      onSave({
        event: selectedEvent,
        conditions: initialConditions,
        status: 'configured',
      });
    }
  };

  const handleSendEvent = useCallback(
    (data: SendEventModalSubmitData) => {
      const payload = {
        event: data.eventName,
        identifier: data.identifier,
        data: data.attributes.reduce(
          (acc, { attribute, value }) => ({ ...acc, [attribute]: value }),
          {} as Record<string, unknown>
        ),
      };
      setSendAttempted(true);
      dispatch(sendEventRequest(payload));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!sendAttempted || sendEventLoading) return;
    if (!sendEventError) {
      setIsSendEventModalOpen(false);
    }
    setSendAttempted(false);
  }, [sendAttempted, sendEventLoading, sendEventError]);

  return (
    <>
      <div className="flex flex-col bg-card rounded-b-lg shadow-sm w-full max-h-[65vh] overflow-y-auto">
        <div className="flex-1 p-4 w-full space-y-4 overflow-y-auto min-h-0">
          <div className="w-full">
            <SelectInput
              label="Event"
              placeholder="Select or type an event name"
              options={eventOptions}
              value={selectedEvent}
              onChange={setSelectedEvent}
            />
          </div>

          {selectedEvent && (
            <EventAttributesCondition
              conditions={initialConditions}
              onConditionsChange={onConditionsChange}
              eventAttributeOptions={attributeOptions}
              loading={eventAttributesLoading}
            />
          )}
        </div>

        <div className="w-full p-4 border-t border-border shrink-0 space-y-3 bg-muted/10">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsSendEventModalOpen(true)}
              className="w-full border-border text-foreground hover:bg-muted/80 text-sm py-2 rounded-lg bg-card"
            >
              Send event
            </Button>
            <Button
              variant="primary"
              disabled={!isValid || !hasModified}
              onClick={handleSave}
              className="w-full bg-primary text-primary-foreground hover:bg-primary-hover border-0 text-sm font-medium py-2.5 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save and Continue
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-border text-foreground hover:bg-muted/80 text-sm py-2 rounded-lg bg-card"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
      <SendEventModal
        isOpen={isSendEventModalOpen}
        onClose={() => setIsSendEventModalOpen(false)}
        currentEventName={selectedEvent}
        onSend={handleSendEvent}
        sendEventLoading={sendEventLoading}
        sendEventError={sendEventError}
      />
    </>
  );
};

export default TriggerEventPanel;
